import type { i18n as I18nInstance } from 'i18next';
import type { BasketTableAnswerItem, QuestionConfigQuestionType, QuestionDataValue } from '/index';

import {
  basketTableTotalTypes,
  questionScopeCategories,
  questionSelectorOptions,
  urlApplicationRoot,
} from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';
import vehicleIcons from '/shared/util/vehicleIcons';

import CarCard from '/business/shared/images/initiatives/Car-card-1x.jpg';
import CarHeader from '/business/shared/images/initiatives/Car-header-1x.jpg';
import vehicleCardBackground from '/business/shared/images/initiatives/CompanyVehicle-card-1x.jpg';
import vehicleBackground from '/business/shared/images/initiatives/CompanyVehicle-card-2x.jpg';
import EVImageCard from '/business/shared/images/initiatives/EV-card-image.jpg';
import EVImageHeader from '/business/shared/images/initiatives/EV-header.jpg';
import TruckCard from '/business/shared/images/initiatives/Truck-card-1x.jpg';
import TruckHeader from '/business/shared/images/initiatives/Trucks-header-1x.jpg';
import VanCard from '/business/shared/images/initiatives/Van-card-1x.jpg';
import VanHeader from '/business/shared/images/initiatives/Vans-header-1x.jpg';

import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import useFormatSharedSupplierScope1 from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedSupplierScope1';
import { initiativeVisuals } from '/business/shared/components/questions/helper/initiativeVisuals';
import { getTranslatedDisplayName } from '/business/shared/components/questions/helper/internals/mappedFieldValues';
import educationContentJson from '/business/shared/components/questions/scope1/_education/company-vehicles.json';
import useVehicleTotalUsageSchema from '/business/shared/components/questions/scope1/companyVeh/_hooks/useVehicleTotalUsageSchema';
import useVehicleSingleSchema from '/business/shared/components/questions/sharedQuestions/_hooks/useVehicleSingleSchema';
import formatVehicleEfficiencyUnit from '/business/shared/functions/formatVehicleEfficiencyUnit';
import DistanceCanonicalName from '/business/shared/components/widgets/misc/DistanceCanonicalName';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';

type ConfigureCompanyVehiclesOptions = {
  i18n: I18nInstance;
  hasScopeBasedOrganisation?: boolean;
  provideTotalFuelPage?: boolean;
  provideVehicleBreakdownPage?: boolean;
  reductionCardsSlides?: string[];
};

type InitiativeVisualProps = Parameters<
  NonNullable<NonNullable<QuestionConfigQuestionType['initiative']>['getVisuals']>
>[0];
type InitiativeSummaryProps = NonNullable<
  Parameters<NonNullable<NonNullable<QuestionConfigQuestionType['initiative']>['formatForInitiativeSummary']>>[0]
>;

type FuelTypeDisplay = {
  displayName: string;
};

type CompanyVehicleTableData = {
  annualDistance?: number | string | null;
  annualDistanceUnit: string;
  distanceTravelled?: number | string | null;
  efficiency?: number | string | null;
  efficiencyUnit?: string;
  fuelId?: string | null;
  fuelType?: FuelTypeDisplay | null;
  harmonisedInjection: (unit: string) => string;
  numOfVehicles?: number | string | null;
  quantity?: number | string | null;
  quantityUnit?: string;
  tonnage?: number | null;
  type?: string | string[];
  vehicleSize?: string;
  vehicleSizeName?: string;
};

type CompanyVehicleRowConfig = {
  defaultLabel: string;
  labelKey: string;
  usesBusCoachDisplay: boolean;
  usesHighMileageDistances: boolean;
};

const companyVehicleRowConfigs = {
  'calculation-travel-emissions-company-vehicles-company-owned-cars': {
    defaultLabel: 'Cars',
    labelKey: 'business:QuestionPage.forms.vehicleTypeSelector.para2',
    usesBusCoachDisplay: false,
    usesHighMileageDistances: false,
  },
  'calculation-travel-emissions-company-vehicles-company-owned-vans': {
    defaultLabel: 'Vans',
    labelKey: 'business:QuestionPage.forms.vehicleTypeSelector.para3',
    usesBusCoachDisplay: false,
    usesHighMileageDistances: false,
  },
  'calculation-travel-emissions-company-vehicles-company-owned-trucks': {
    defaultLabel: 'Trucks',
    labelKey: 'business:QuestionPage.forms.vehicleTypeSelector.para4',
    usesBusCoachDisplay: false,
    usesHighMileageDistances: true,
  },
  'calculation-travel-emissions-company-vehicles-company-owned-buses-coaches': {
    defaultLabel: 'Buses / coaches',
    labelKey: 'business:QuestionPage.forms.vehicleTypeSelector.para5',
    usesBusCoachDisplay: true,
    usesHighMileageDistances: true,
  },
} satisfies Record<string, CompanyVehicleRowConfig>;

type CompanyVehicleRowType = keyof typeof companyVehicleRowConfigs;

const isCompanyVehicleRowType = (type: string): type is CompanyVehicleRowType => {
  return type in companyVehicleRowConfigs;
};

const busCoachFuelDisplayNames: Record<string, string> = {
  'distance-diesel': 'Diesel',
  'distance-hybrid': 'Diesel Hybrid',
  'distance-ev': 'Electric',
};

function getNumberValue(value: QuestionDataValue): number {
  return typeof value === 'number' ? value : 0;
}

export const getConfigureCompanyVehicles = ({
  i18n,
  hasScopeBasedOrganisation = true,
  provideTotalFuelPage = true,
  provideVehicleBreakdownPage = true,
  reductionCardsSlides = ['slide1', 'slide2', 'slide3', 'slide4', 'slide5'],
}: ConfigureCompanyVehiclesOptions): QuestionConfigQuestionType => {
  const scopeCategory = hasScopeBasedOrganisation
    ? questionScopeCategories.SCOPE_1
    : questionScopeCategories.TRAVEL_EMISSIONS;
  const calculateVehicleTotal = (allItems: BasketTableAnswerItem[]) => {
    return allItems.reduce((accumulator, item) => {
      const numOfVehicles = getNumberValue(item?.answer?.genericData?.numOfVehicles);
      if (numOfVehicles > 0) {
        return accumulator + numOfVehicles;
      }
      return accumulator;
    }, 0);
  };
  const totalFuelTypeCell = ({ fuelId, fuelType }: CompanyVehicleTableData) => fuelId || fuelType?.displayName;
  const translatedFuelTypeCell = ({ fuelType, fuelId }: CompanyVehicleTableData) =>
    fuelType
      ? `${i18n.t(`business:emissionFactors.vehicleFuelTypes.${fuelType?.displayName}`, fuelType?.displayName)}`
      : fuelId;
  const busCoachFuelTypeCell = ({ fuelType, fuelId }: CompanyVehicleTableData) => {
    const fuelName = fuelType?.displayName ?? fuelId;
    return fuelName ? (busCoachFuelDisplayNames[fuelName] ?? fuelName) : fuelName;
  };
  const getVehicleRowConfig = ({ type }: CompanyVehicleTableData) => {
    const answerType = Array.isArray(type) ? type.join('-') : type;
    return answerType && isCompanyVehicleRowType(answerType) ? companyVehicleRowConfigs[answerType] : undefined;
  };
  const vehicleTypeCell = (tableData: CompanyVehicleTableData) => {
    const rowConfig = getVehicleRowConfig(tableData);
    return rowConfig ? i18n.t(rowConfig.labelKey, rowConfig.defaultLabel) : '-';
  };
  const vehicleFuelTypeCell = (tableData: CompanyVehicleTableData) => {
    return getVehicleRowConfig(tableData)?.usesBusCoachDisplay
      ? busCoachFuelTypeCell(tableData)
      : translatedFuelTypeCell(tableData);
  };
  const quantityCell = ({
    numOfVehicles,
    quantity,
    quantityUnit = 'units',
    harmonisedInjection,
  }: CompanyVehicleTableData) => {
    if (numOfVehicles) {
      return numOfVehicles;
    }
    if (quantity) {
      return `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(quantityUnit), quantityUnit)} ${harmonisedInjection(quantityUnit)}`;
    }
  };
  const totalFuelQuantityCell = ({
    quantity,
    quantityUnit = 'units',
    harmonisedInjection,
    numOfVehicles,
  }: CompanyVehicleTableData) => {
    if (!quantity) {
      return numOfVehicles;
    }
    return `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(quantityUnit), quantityUnit)} ${harmonisedInjection(quantityUnit)}`;
  };
  const vehicleSizeCell = ({ vehicleSize, vehicleSizeName, efficiency, efficiencyUnit }: CompanyVehicleTableData) => {
    if (!vehicleSize && !efficiency) {
      return '-';
    }
    const vehicleSizeLabel = vehicleSize ?? '';
    return efficiency
      ? `${efficiency} ${formatVehicleEfficiencyUnit(i18n, efficiencyUnit)}`
      : getTranslatedDisplayName(
          vehicleSizeLabel,
          vehicleSizeName ? `business:emissionFactors.vehicleSizes.${vehicleSizeName}` : undefined,
          i18n.t,
          vehicleSizeLabel,
        );
  };
  const busCoachVehicleSizeCell = ({ vehicleSize }: CompanyVehicleTableData) => vehicleSize || '-';
  const vehicleSizeDisplayCell = (tableData: CompanyVehicleTableData) => {
    return getVehicleRowConfig(tableData)?.usesBusCoachDisplay
      ? busCoachVehicleSizeCell(tableData)
      : vehicleSizeCell(tableData);
  };
  const annualDistanceCell = (tableData: CompanyVehicleTableData) => {
    const { annualDistance, annualDistanceUnit } = tableData;
    if (!annualDistance) {
      return '-';
    }
    return (
      <DistanceCanonicalName
        distance={annualDistance}
        unit={annualDistanceUnit}
        type="annual"
        usesHighMileageDistances={getVehicleRowConfig(tableData)?.usesHighMileageDistances}
      />
    );
  };
  const tonnageCell = ({ tonnage }: CompanyVehicleTableData) => `${formatNumber(tonnage)} tCO\u2082e`;

  const questionEnums = {
    TOTAL_FUEL: {
      id: 'total-fuel-consumption',
      title: i18n.t('business:Questions.travel.question1.subQuestion1.title'),
      apiKey: 'calculation-travel-emissions-company-vehicles-total-fuel-consumption',
      hideParentBreadcrumb: true,
      scopeCategory,
      exclusiveWith: [
        'calculation-travel-emissions-company-vehicles-company-owned-cars',
        'calculation-travel-emissions-company-vehicles-company-owned-vans',
        'calculation-travel-emissions-company-vehicles-company-owned-trucks',
        'calculation-travel-emissions-company-vehicles-company-owned-buses-coaches',
      ],
      exclusiveWithLabel: i18n.t('business:Modals.questionSubmissionConfirmation.fuelEfficiencyDistance'),
      table: {
        title: i18n.t('business:Questions.travel.question1.subQuestion1.table.title'),
        totalType: basketTableTotalTypes.TONNAGE,
        headings: [
          i18n.t('business:Questions.travel.question1.subQuestion1.table.heading1'),
          i18n.t('business:Questions.travel.question1.subQuestion1.table.heading2'),
          i18n.t('business:Questions.travel.question1.subQuestion1.table.heading3'),
          i18n.t('business:Questions.travel.question1.subQuestion1.table.heading4'),
          i18n.t('business:Questions.travel.question1.subQuestion1.table.heading5'),
          '',
        ],
        cells: [
          totalFuelTypeCell,
          totalFuelQuantityCell,
          vehicleSizeCell,
          ({ distanceTravelled }: CompanyVehicleTableData) => (distanceTravelled ? distanceTravelled : '-'),
          tonnageCell,
        ],
        isAnswerEditable: false,
        supplier: {
          columns: ['emissionSource', 'supplierName', 'scope1orAnd3Intensity', 'quantity', 'carbonEmissions'],
        },
        custom: {
          columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
        },
      },
      details: {},
      initiative: {
        getVisuals: ({ frontendEnum, questionTypeKey = null, isBpOffering }: InitiativeVisualProps) => {
          if (!isBpOffering) {
            return {
              description: false,
              background: vehicleBackground,
              useMask: false,
              cardBackground: vehicleCardBackground,
            };
          }
          if (isBpOffering === true) {
            const { cardImage, headerImage } = initiativeVisuals(frontendEnum, questionTypeKey);
            return {
              description: false,
              background: headerImage,
              useMask: false,
              cardBackground: cardImage,
            };
          }
        },
        formatForInitiativeSummary: ({ initiativeAnswerEnum = null }: InitiativeSummaryProps = {}) => {
          return {
            isShowingReportingPeriod: false,
            isShowingReportingPeriodNoReduction: true,
            category: initiativeAnswerEnum,
            description: `Remaining ${initiativeAnswerEnum} consumption for company vehicles to reduce after incorporating the initiatives already added to the plan so far`,
            descriptionNoReduction: `${initiativeAnswerEnum} consumption for company vehicles during the reporting period `,
          };
        },
      },
      useHookLoader: useVehicleTotalUsageSchema,
      activitySubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter data about fuel used by vehicles owned or controlled by your business during the reporting period:{' '}
            <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
    },
    COMPANY_CARS: {
      id: 'company-owned-cars',
      title: i18n.t('business:Questions.travel.question1.subQuestion2.title', 'Company owned cars'),
      apiKey: 'calculation-travel-emissions-company-vehicles-company-owned-cars',
      exclusiveWith: ['calculation-travel-emissions-company-vehicles-total-fuel-consumption'],
      exclusiveWithLabel: i18n.t('business:Modals.questionSubmissionConfirmation.totalFuelConsumption'),
      useHookLoader: useVehicleSingleSchema,
      displayNumOfVehicles: true,
      scopeCategory,
      initiative: {
        getVisuals: ({ frontendEnum, isBpOffering }: InitiativeVisualProps) => {
          if (!isBpOffering) {
            return {
              description: false,
              background: vehicleBackground,
              useMask: false,
              cardBackground: vehicleCardBackground,
            };
          }
          if (isBpOffering === true) {
            if (frontendEnum?.includes('to-ev')) {
              return {
                description: false,
                background: EVImageHeader,
                useMask: false,
                cardBackground: EVImageCard,
              };
            }
            if (!frontendEnum?.includes('to-ev')) {
              return {
                description: false,
                background: CarHeader,
                useMask: false,
                cardBackground: CarCard,
              };
            }
          }
        },
        formatForInitiativeSummary: ({ initiativeAnswerEnum = null }: InitiativeSummaryProps = {}) => {
          return {
            isShowingReportingPeriod: false,
            isShowingReportingPeriodNoReduction: true,
            category: initiativeAnswerEnum,
            description: `Remaining ${initiativeAnswerEnum} consumption for company vehicles - cars to reduce after incorporating the initiatives already added to the plan so far`,
            descriptionNoReduction: `${initiativeAnswerEnum} consumption for company vehicles - cars during the reporting period `,
          };
        },
      },
      table: {
        title: i18n.t('business:Questions.travel.question1.subQuestion2.table.title'),
        calculateTotal: calculateVehicleTotal,
        totalType: basketTableTotalTypes.COUNT,
        headings: [
          i18n.t('business:QuestionPage.forms.vehicleTypeSelector.tableHeading', 'Vehicle type'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading1'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading4'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading2'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading3'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading5'),
        ],
        cells: [
          vehicleTypeCell,
          vehicleFuelTypeCell,
          quantityCell,
          vehicleSizeDisplayCell,
          annualDistanceCell,
          tonnageCell,
        ],
        isAnswerEditable: false,
        supplier: {
          columns: ['emissionSource', 'supplierName', 'scope1orAnd3Intensity', 'quantity', 'carbonEmissions'],
        },
        custom: {
          columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
        },
      },
      details: {},
      activitySubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter data about fuel used by vehicles owned or controlled by your business during the reporting period:{' '}
            <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
    },
    COMPANY_TRUCK_VAN_POLYFILL: {
      id: 'company-owned-vans-trucks',
      redirect: () => `/${urlApplicationRoot}/questions/calculation/travel-emissions/company-vehicles`,
      scopeCategory,
    },
    COMPANY_VANS: {
      id: 'company-owned-vans',
      title: i18n.t('business:Questions.travel.question1.subQuestion3.title'),
      apiKey: 'calculation-travel-emissions-company-vehicles-company-owned-vans',
      exclusiveWith: ['calculation-travel-emissions-company-vehicles-total-fuel-consumption'],
      exclusiveWithLabel: i18n.t('business:Modals.questionSubmissionConfirmation.totalFuelConsumption'),
      useHookLoader: useVehicleSingleSchema,
      displayNumOfVehicles: true,
      scopeCategory,
      table: {
        title: i18n.t('business:Questions.travel.question1.subQuestion3.table.title'),
        calculateTotal: calculateVehicleTotal,
        totalType: basketTableTotalTypes.COUNT,
        headings: [
          i18n.t('business:QuestionPage.forms.vehicleTypeSelector.tableHeading', 'Vehicle type'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading1'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading4'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading2'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading3'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading5'),
        ],
        cells: [
          vehicleTypeCell,
          vehicleFuelTypeCell,
          quantityCell,
          vehicleSizeDisplayCell,
          annualDistanceCell,
          tonnageCell,
        ],
        isAnswerEditable: false,
      },
      details: {},
      initiative: {
        getVisuals: ({ frontendEnum, isBpOffering }: InitiativeVisualProps) => {
          if (!isBpOffering) {
            return {
              description: false,
              background: vehicleBackground,
              useMask: false,
              cardBackground: vehicleCardBackground,
            };
          }
          if (isBpOffering === true) {
            if (frontendEnum?.includes('to-ev')) {
              return {
                description: false,
                background: EVImageHeader,
                useMask: false,
                cardBackground: EVImageCard,
              };
            }
            if (!frontendEnum?.includes('to-ev')) {
              return {
                description: false,
                background: VanHeader,
                useMask: false,
                cardBackground: VanCard,
              };
            }
          }
        },
        formatForInitiativeSummary: ({ initiativeAnswerEnum = null }: InitiativeSummaryProps = {}) => {
          return {
            isShowingReportingPeriod: false,
            isShowingReportingPeriodNoReduction: true,
            category: initiativeAnswerEnum,
            description: `Remaining ${initiativeAnswerEnum} consumption for company vehicles - vans to reduce after incorporating the initiatives already added to the plan so far`,
            descriptionNoReduction: `${initiativeAnswerEnum} consumption for company vehicles - vans during the reporting period `,
          };
        },
      },
      activitySubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter data about fuel used by vehicles owned or controlled by your business during the reporting period:{' '}
            <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
    },
    COMPANY_TRUCKS: {
      id: 'company-owned-trucks',
      title: i18n.t('business:Questions.travel.question1.subQuestion4.title'),
      apiKey: 'calculation-travel-emissions-company-vehicles-company-owned-trucks',
      exclusiveWith: ['calculation-travel-emissions-company-vehicles-total-fuel-consumption'],
      exclusiveWithLabel: i18n.t('business:Modals.questionSubmissionConfirmation.totalFuelConsumption'),
      useHookLoader: useVehicleSingleSchema,
      displayNumOfVehicles: true,
      scopeCategory,
      table: {
        title: i18n.t('business:Questions.travel.question1.subQuestion4.table.title'),
        calculateTotal: calculateVehicleTotal,
        totalType: basketTableTotalTypes.COUNT,
        headings: [
          i18n.t('business:QuestionPage.forms.vehicleTypeSelector.tableHeading', 'Vehicle type'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading1'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading4'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading2'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading3'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading5'),
        ],
        cells: [
          vehicleTypeCell,
          vehicleFuelTypeCell,
          quantityCell,
          vehicleSizeDisplayCell,
          annualDistanceCell,
          tonnageCell,
        ],
        isAnswerEditable: false,
      },
      details: {},
      initiative: {
        getVisuals: ({ frontendEnum, isBpOffering }: InitiativeVisualProps) => {
          if (!isBpOffering) {
            return {
              description: false,
              background: vehicleBackground,
              useMask: false,
              cardBackground: vehicleCardBackground,
            };
          }
          if (isBpOffering) {
            if (frontendEnum?.includes('to-ev')) {
              return {
                description: false,
                background: EVImageHeader,
                useMask: false,
                cardBackground: EVImageCard,
              };
            }
            if (!frontendEnum?.includes('to-ev')) {
              return {
                description: false,
                background: TruckHeader,
                useMask: false,
                cardBackground: TruckCard,
              };
            }
          }
        },
        formatForInitiativeSummary: ({ initiativeAnswerEnum = null }: InitiativeSummaryProps = {}) => {
          return {
            isShowingReportingPeriod: false,
            isShowingReportingPeriodNoReduction: true,
            category: initiativeAnswerEnum,
            description: `Remaining ${initiativeAnswerEnum} consumption for company vehicles - trucks to reduce after incorporating the initiatives already added to the plan so far`,
            descriptionNoReduction: `${initiativeAnswerEnum} consumption for company vehicles - trucks during the reporting period `,
          };
        },
      },
      activitySubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter data about fuel used by vehicles owned or controlled by your business during the reporting period:{' '}
            <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
    },
    COMPANY_BUSES_COACHES: {
      id: 'company-owned-buses-coaches',
      title: i18n.t('business:Questions.travel.question1.subQuestion6.title'),
      apiKey: 'calculation-travel-emissions-company-vehicles-company-owned-buses-coaches',
      exclusiveWith: ['calculation-travel-emissions-company-vehicles-total-fuel-consumption'],
      exclusiveWithLabel: i18n.t('business:Modals.questionSubmissionConfirmation.totalFuelConsumption'),
      useHookLoader: useVehicleSingleSchema,
      displayNumOfVehicles: true,
      scopeCategory,
      table: {
        title: i18n.t('business:Questions.travel.question1.subQuestion6.table.title'),
        calculateTotal: calculateVehicleTotal,
        totalType: basketTableTotalTypes.COUNT,
        headings: [
          i18n.t('business:QuestionPage.forms.vehicleTypeSelector.tableHeading', 'Vehicle type'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading1'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading4'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading2'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading3'),
          i18n.t('business:Questions.travel.question1.subQuestion2.table.heading5'),
        ],
        cells: [
          vehicleTypeCell,
          vehicleFuelTypeCell,
          quantityCell,
          vehicleSizeDisplayCell,
          annualDistanceCell,
          tonnageCell,
        ],
        isAnswerEditable: false,
      },
      details: {},
      initiative: {
        getVisuals: ({ frontendEnum, isBpOffering }: InitiativeVisualProps) => {
          if (!isBpOffering) {
            return {
              description: false,
              background: vehicleBackground,
              useMask: false,
              cardBackground: vehicleCardBackground,
            };
          }
          if (isBpOffering) {
            if (frontendEnum?.includes('to-ev')) {
              return {
                description: false,
                background: EVImageHeader,
                useMask: false,
                cardBackground: EVImageCard,
              };
            }
            if (!frontendEnum?.includes('to-ev')) {
              return {
                description: false,
                background: TruckHeader,
                useMask: false,
                cardBackground: TruckCard,
              };
            }
          }
        },
        formatForInitiativeSummary: ({ initiativeAnswerEnum = null }: InitiativeSummaryProps = {}) => {
          return {
            isShowingReportingPeriod: false,
            isShowingReportingPeriodNoReduction: true,
            category: initiativeAnswerEnum,
            description: `Remaining ${initiativeAnswerEnum} consumption for company vehicles - buses and coaches to reduce after incorporating the initiatives already added to the plan so far`,
            descriptionNoReduction: `${initiativeAnswerEnum} consumption for company vehicles - buses and coaches during the reporting period `,
          };
        },
      },
      activitySubheader: (
        <>
          <StyledParagraph $largeText $hasExtraMarginBottom>
            Enter data about fuel used by vehicles owned or controlled by your business during the reporting period:{' '}
            <ReportingPeriod $green />
          </StyledParagraph>
          <StyledInfoFlash>
            <div>For more information, refer to the "Help" section for detailed guidance.</div>
          </StyledInfoFlash>
        </>
      ),
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
    },
  };

  const applicableSubQuestions: QuestionConfigQuestionType[] = [];
  if (provideTotalFuelPage && provideVehicleBreakdownPage) {
    applicableSubQuestions.push({
      id: 'company-owned-vehicles',
      title: i18n.t('business:Questions.travel.question1.subQuestion5.title'),
      initiativeCategoryOverride: 'company-vehicles',
      initiativeHideWhenEmpty: true,
      scopeCategory,
      isSelectorPage: true,
      splitQuestions: true,

      breadcrumbTitle: i18n.t('business:Questions.travel.question1.subQuestion5.breadcrumbTitle'),
      form: import('/business/shared/components/questions/scope1/companyVeh/TypeOfVehicleQuestion'),
      subQuestions: [
        questionEnums.COMPANY_CARS,
        questionEnums.COMPANY_VANS,
        questionEnums.COMPANY_TRUCKS,
        questionEnums.COMPANY_BUSES_COACHES,
        questionEnums.TOTAL_FUEL,
      ],
      education: {
        hasEducationWidget: true,
        educationContent: educationContentJson,
      },
    });
  } else if (provideTotalFuelPage) {
    applicableSubQuestions.push(questionEnums.TOTAL_FUEL);
  } else if (provideVehicleBreakdownPage) {
    applicableSubQuestions.push(questionEnums.COMPANY_TRUCK_VAN_POLYFILL);
    applicableSubQuestions.push({
      id: 'company-owned-vehicles',
      title: i18n.t('business:Questions.travel.question1.subQuestion5.title'),
      initiativeCategoryOverride: 'company-vehicles',
      initiativeHideWhenEmpty: true,
      scopeCategory,
      isSelectorPage: true,
      splitQuestions: true,

      breadcrumbTitle: i18n.t('business:Questions.travel.question1.subQuestion5.breadcrumbTitle'),
      form: import('/business/shared/components/questions/scope1/companyVeh/TypeOfVehicleQuestion'),
      subQuestions: [
        questionEnums.COMPANY_CARS,
        questionEnums.COMPANY_VANS,
        questionEnums.COMPANY_TRUCKS,
        questionEnums.COMPANY_BUSES_COACHES,
      ],
    });
  }

  return {
    id: 'company-vehicles',
    scopeCategory,
    title: i18n.t('business:Questions.travel.question1.title'),
    icon: vehicleIcons.van,
    initiativeHideWhenEmpty: true,

    isCompletePropName: 'isCompanyVehiclesComplete',
    form: import('/business/shared/components/questions/scope1/companyVeh/TravelVehHasDataQuestion'),
    primaryI18nKey: 'business:QuestionPage.forms.travelEmissionsSelector.para3',
    subTextI18nKey: 'business:QuestionPage.forms.travelEmissionsSelector.para4',
    overviewPageTitleI18nKey: 'business:Questions.travel.question1.title',
    reductionPageKey: 'companyVehicles',
    reductionPageCards: reductionCardsSlides,
    subQuestions: applicableSubQuestions,
    selector: {
      questionBaseRootApiKey: 'calculation-travel-emissions-company-vehicles-total-fuel-consumption',
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with vehicles owned and controlled by your business, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: i18n.t('business:Questions.selectors.companyVehicles.activity.requiredData'),
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData: i18n.t('business:Questions.selectors.companyVehicles.supplier.requiredData'),
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData: i18n.t('business:Questions.selectors.companyVehicles.custom.requiredData'),
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
    },
    useQuestionBranchSupplierLoader: () =>
      useFormatSharedSupplierScope1({
        questionFormatName: 'vehicles',
        emissionSourcePlaceholder: 'Select type of fuel',
        sourceErrMsg: i18n.t('business:Errors.supplierForm.fuelSource'),
      }),
    useQuestionBranchCustomLoader: () => useFormatSharedCustomScope1({ questionFormatName: 'vehicles' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about fuel used by vehicles owned or controlled by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
